/**
 * A search's results as a full PAGE over the one the person is on (the user, 2026-11-02: "the search page should
 * be displayed where the user is, don't take it anywhere else"; "designed in our design"). The frame both results
 * pages share — all of MyRave's (`SearchPage.tsx`) and a rave's (`visitor/RaveResults.tsx`): over the page's own
 * box — under the header, beside a docked menu (`main`), to the bottom of the screen — the words as the title with
 * how many, ✕ (Escape too), and the sections.
 *
 * The header's field stays the search: the page only shows what it is handed.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '@/i18n';
import { SpinnerMark } from '@/ui';
import './ResultsPage.css';

/**
 * How many results pages are on screen. A page DRAWN while another still is, is a SWITCH between the two searches
 * (the user, 2026-11-02: "switching between this rave and all of MyRave shows the page underneath"): it shows at
 * once, without the fade-in that let the page underneath through, and it takes the history step over rather than
 * adding a second one. Counted from the layout effects, because the closing page's own clean-up runs only AFTER the
 * new one is drawn — a close time it wrote came too late (the first try).
 */
let onScreen = 0;

/** Online now — `navigator.onLine`, followed. All of MyRave's search needs it; a rave's never does. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine !== false);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  return online;
}

/** One of the two searches, as a tab: the rave's (offline too) and all of MyRave's (online only). */
export interface ResultsTab {
  readonly key: string;
  readonly label: string;
  readonly current: boolean;
  /** Absent: not now — offline, for all of MyRave. */
  readonly onPick?: () => void;
  /** Said beside a tab that cannot be had now — "Offline". */
  readonly note?: string;
}

export function ResultsPage({ eyebrow, title, count, tabs, onClose, children }: {
  /** Where it searched — "All of MyRave", the rave's name. */
  eyebrow: string;
  title: string;
  /** "12 results" — absent while it is still looking. */
  count?: string;
  /** Inside a rave: this rave · all of MyRave. */
  tabs?: readonly ResultsTab[];
  onClose: () => void;
  children: ReactNode;
}) {
  const tr = useT();
  /* Over the page's own box: under the header, beside a docked menu — measured, as either is any size. */
  const [box, setBox] = useState({ top: 0, left: 0, right: 0 });
  useLayoutEffect(() => {
    const main = document.querySelector('main');
    const measure = () => {
      const header = document.querySelector('header')?.getBoundingClientRect();
      const area = main?.getBoundingClientRect();
      setBox({
        top: Math.round(header?.bottom ?? 0),
        left: Math.max(0, Math.round(area?.left ?? 0)),
        right: Math.max(0, Math.round(area ? window.innerWidth - area.right : 0)),
      });
    };
    measure();
    window.addEventListener('resize', measure);
    // The menu docking or leaving moves the page's edge without a window resize.
    const watch = main && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (main) watch?.observe(main);
    return () => { window.removeEventListener('resize', measure); watch?.disconnect(); };
  }, []);
  /*
   * Always a way out (the user, 2026-11-02: "can't exit the search page"). On a phone the header slid away on a
   * scroll and came back OVER the page's ✕, and Back left the rave instead of the page. So while it is open the
   * header is held where it is (`data-header-hold`, `scrollChrome.tsx`), and the page is a step in the history:
   * Back closes it; closed any other way, that step is taken back.
   */
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [switched] = useState(() => onScreen > 0);
  useLayoutEffect(() => {
    onScreen += 1;
    return () => { onScreen -= 1; };
  }, []);
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.headerHold = '';
    delete root.dataset.headerAway;
    const mark = { myraveResults: Math.random() };
    // A switch takes over the step the other page made; a fresh page adds one.
    if ((window.history.state as typeof mark | null)?.myraveResults !== undefined) window.history.replaceState(mark, '');
    else window.history.pushState(mark, '');
    const onBack = () => closeRef.current();
    window.addEventListener('popstate', onBack);
    return () => {
      delete root.dataset.headerHold;
      window.removeEventListener('popstate', onBack);
      // Closed by ✕ or Escape: take our step back, so Back then leaves the page as before — a moment later, and only
      // if it is still the current step (StrictMode's second mount has pushed its own by then).
      setTimeout(() => {
        if ((window.history.state as typeof mark | null)?.myraveResults === mark.myraveResults) window.history.back();
      }, 0);
    };
  }, []);
  // Escape from anywhere on the page — the header's field has its own. Caught first and marked used, so it closes
  // the page and not the menu too (the menu skips an Escape already used).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || e.target instanceof HTMLInputElement) return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return createPortal(
    <section className="results-page" data-no-pull data-switched={switched || undefined} style={{ top: box.top, left: box.left, right: box.right }} aria-label={title}>
      <div className="results-page-inner">
        <header className="results-page-head">
          <div className="results-page-heading">
            <span className="results-page-eyebrow">{eyebrow}</span>
            <h1 className="results-page-title">{title}</h1>
            {count && <span className="results-page-count"><span className="results-page-count-dot" aria-hidden="true" />{count}</span>}
          </div>
          <button type="button" className="results-page-close" onClick={onClose} aria-label={tr('Close')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </header>
        {tabs && tabs.length > 1 && (
          <div className="results-page-tabs" role="tablist" aria-label={tr('Search')}>
            {tabs.map((t) => (
              <button
                key={t.key} type="button" role="tab" aria-selected={t.current} className="results-page-tab"
                disabled={!t.current && !t.onPick} onClick={t.current ? undefined : t.onPick}
              >
                {t.label}
                {t.note && <span className="results-page-tab-note">{t.note}</span>}
              </button>
            ))}
          </div>
        )}
        {children}
      </div>
    </section>,
    document.body,
  );
}

/** One kind of result under its heading — "Raves", "Artists", "Sets". */
export function ResultsSection({ title, count, children }: { title: string; count?: number; children: ReactNode }) {
  return (
    <section className="results-page-section">
      <h2 className="results-page-group">
        {title}
        {count !== undefined && <span className="results-page-group-count">{count}</span>}
      </h2>
      {children}
    </section>
  );
}

/**
 * Still looking — MyRave's own loading mark on the page's dark ground (the user, 2026-11-02: "when switching keep
 * showing the search page's black background and show MyRave loading"), never a blank or the page underneath.
 */
export function ResultsLoading({ label }: { label: string }) {
  return (
    <div className="results-page-loading" role="status">
      <span className="results-page-loading-mark" aria-hidden="true"><SpinnerMark /></span>
      <span className="results-page-loading-label">{label}</span>
    </div>
  );
}
